import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface MonumentCompany {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  shipping_address: string | null;
  shipping_street_address: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_zip: string | null;
  website: string | null;
  is_active: boolean;
  offers_cleaning: boolean;
  offers_repairs: boolean;
  use_different_shipping_address: boolean;
  linked_fulfillment_partner_id: string | null;
  commission_rate: number;
  medallion_unit_price_cents: number | null;
}

export type PortalPermission = 'view_earnings' | 'view_team' | 'manage_settings' | 'order_medallions';

interface PortalAuthState {
  isPortalUser: boolean;
  isLoading: boolean;
  company: MonumentCompany | null;
  role: 'owner' | 'staff' | null;
  isOwner: boolean;
  permissions: Set<PortalPermission>;
  hasPermission: (permission: PortalPermission) => boolean;
  isDeactivated: boolean;
}

const PortalAuthContext = createContext<PortalAuthState | null>(null);

export const PortalAuthProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [isPortalUser, setIsPortalUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState<MonumentCompany | null>(null);
  const [role, setRole] = useState<'owner' | 'staff' | null>(null);
  const [permissions, setPermissions] = useState<Set<PortalPermission>>(new Set());
  const initialLoadDone = useRef(false);

  useEffect(() => {
    const checkPortalAccess = async () => {
      if (authLoading) return;

      if (!user) {
        setIsPortalUser(false);
        setCompany(null);
        setRole(null);
        setPermissions(new Set());
        setIsLoading(false);
        initialLoadDone.current = false;
        return;
      }

      // Only show loading spinner on initial load, not on re-validations
      if (!initialLoadDone.current) {
        setIsLoading(true);
      }

      try {
        const { data: isMonumentUser, error: checkError } = await supabase
          .rpc('is_monument_company_user', { _user_id: user.id });

        if (checkError) {
          console.error('Error checking portal access:', checkError);
          setIsPortalUser(false);
          setIsLoading(false);
          return;
        }

        setIsPortalUser(!!isMonumentUser);

        if (isMonumentUser) {
          const { data: companyId, error: companyIdError } = await supabase
            .rpc('get_user_monument_company', { _user_id: user.id });

          if (companyIdError || !companyId) {
            console.error('Error getting company ID:', companyIdError);
            setIsLoading(false);
            return;
          }

          const { data: companyData, error: companyError } = await supabase
            .from('monument_companies')
            .select('*')
            .eq('id', companyId)
            .single();

          if (companyError) {
            console.error('Error fetching company:', companyError);
          } else {
            setCompany(companyData);
          }

          const { data: isOwnerResult, error: ownerError } = await supabase
            .rpc('is_monument_company_owner', {
              _user_id: user.id,
              _company_id: companyId
            });

          if (ownerError) {
            console.error('Error checking owner status:', ownerError);
          } else {
            const userRole = isOwnerResult ? 'owner' : 'staff';
            setRole(userRole);

            if (isOwnerResult) {
              setPermissions(new Set(['view_earnings', 'view_team', 'manage_settings', 'order_medallions']));
            } else {
              const { data: companyUser, error: companyUserError } = await supabase
                .from('monument_company_users')
                .select('id')
                .eq('user_id', user.id)
                .eq('company_id', companyId)
                .single();

              if (companyUserError) {
                console.error('Error fetching company user:', companyUserError);
                setPermissions(new Set());
              } else if (companyUser) {
                const { data: userPermissions, error: permError } = await supabase
                  .from('monument_company_user_permissions')
                  .select('permission')
                  .eq('company_user_id', companyUser.id);

                if (permError) {
                  console.error('Error fetching permissions:', permError);
                  setPermissions(new Set());
                } else {
                  const permSet = new Set<PortalPermission>(
                    (userPermissions || []).map(p => p.permission as PortalPermission)
                  );
                  setPermissions(permSet);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Portal auth error:', error);
        setIsPortalUser(false);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };

    checkPortalAccess();
  }, [user, authLoading]);

  const hasPermission = (permission: PortalPermission): boolean => {
    return permissions.has(permission);
  };

  const value: PortalAuthState = {
    isPortalUser,
    isLoading: isLoading || authLoading,
    company,
    role,
    isOwner: role === 'owner',
    permissions,
    hasPermission,
    isDeactivated: company ? !company.is_active : false,
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuthContext = (): PortalAuthState => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuthContext must be used within a PortalAuthProvider');
  }
  return context;
};
